import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Button, CircularProgress, Container, Stack, Tab } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as DrainPipeHelper from '../api/drainPipe/drainPipeHelper';
import { ICreateProjectRequest } from '../api/project/iApiResponse';
import * as ProjectHelper from '../api/project/projectHelper';
import Engine from '../design/engine/Index';
import { route } from '../route/Index';
import Span from '../shared/component/Span';
import { EEngineType } from '../shared/enum';
import isFulfilledProject from '../shared/hoc/isFulfilledProject';
import InputDischarge from '../shared/input/discharge/InputDischarge';
import InputDrainageTrench from '../shared/input/drainageTrench/InputDrainageTrench';
import InputLoad from '../shared/input/InputLoad';
import InputSlope from '../shared/input/InputSlope';
import InputSoil from '../shared/input/soil/InputSoil';
import variable from '../shared/style/variable';
import { dialogAsync } from '../shared/util';
import { cleanAlert } from '../store/alert/alertSlice';
import { selectDrainPipe, setDrainPipe } from '../store/drainPipe/drainPipeSlice';
import { EReduxState } from '../store/enum';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { cleanImage } from '../store/Image/imageSlice';
import { selectProject, setProject } from '../store/project/projectSlice';
import { cleanResult } from '../store/result/resultSlice';

interface IGeometryProps {}

const DesignContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const Geometry = (props: IGeometryProps) => {
    const project = useAppSelector(selectProject);
    const drainPipe = useAppSelector(selectDrainPipe);

    const dispatch = useAppDispatch();

    const [value, setValue] = React.useState('1');
    const [isLoading, setIsLoading] = React.useState(false);

    const { t } = useTranslation();

    const navigate = useNavigate();

    const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;

    const handleGoToDesignCondition = async () => {
        let payload: ICreateProjectRequest = {
            project: project.data!,
        };
        setIsLoading(true);
        let res = await ProjectHelper.createProjectAsync(payload);
        dispatch(setProject(res.data));
        if (drainPipe.status == EReduxState.initialState) {
            let resDrainPipe = await DrainPipeHelper.getDrainPipeForCountryAsync(res.data.projectInfo.projectCountryCode!);
            dispatch(setDrainPipe(resDrainPipe.data));
        }
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());
        navigate(route.design.replace(':projectId', res.data.id!.toString()));
        setIsLoading(false);
    };

    const handleClickApply = async () => {
        if (!project.data?.isBase) {
            dialogAsync('warning', t('areYouSure'), t('warningCreateNewProject'), handleGoToDesignCondition);
        } else {
            handleGoToDesignCondition();
        }
    };

    return (
        <DesignContainer maxWidth={false}>
            <Stack height={'100%'} direction={'row'} spacing={0.5}>
                {isLoading && <CircularProgress />}
                {!isLoading && (
                    <>
                        <Stack sx={{ width: { xs: '30%', md: '24%', backgroundColor: variable.grey_100 } }} position='relative'>
                            <Stack variant='scrollable'>
                                <InputSlope />
                                <InputSoil />
                                <InputLoad />
                                <Stack display={'none'}>
                                    <InputDrainageTrench />
                                    <InputDischarge />
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack sx={{ width: { xs: '70%', md: '76%' } }} direction={'column'} justifyContent={'space-between'}>
                            <Stack height={'100%'}>
                                <TabContext value={value}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Stack direction={'row'} justifyContent={'space-between'} padding={'0 1rem'}>
                                            <Stack direction={'row'} sx={{ width: { xs: '55%', sm: '70%', md: '80%', lg: 'auto' } }}>
                                                <TabList variant='scrollable' onChange={handleChangeTab} aria-label='Engine tab'>
                                                    <Tab
                                                        variant='standard'
                                                        label={
                                                            <Stack>
                                                                <Stack>{t('slopeTransversalEngine')}</Stack>
                                                                <Stack>{t('primaryDrain')}</Stack>
                                                            </Stack>
                                                        }
                                                        value='1'
                                                    />
                                                    {hasSecondaryDrainageTrench && (
                                                        <Tab
                                                            variant='standard'
                                                            label={
                                                                <Stack>
                                                                    <Stack>{t('slopeTransversalEngine')}</Stack>
                                                                    <Stack>{t('secondaryDrain')}</Stack>
                                                                </Stack>
                                                            }
                                                            value='2'
                                                        />
                                                    )}
                                                    <Tab variant='standard' label={t('slopeLongitudinalEngine')} value='3' />
                                                </TabList>
                                            </Stack>
                                            <Stack direction={'row'} justifyContent={'end'}>
                                                <Button variant='green' startIcon={<PlayArrowIcon color='orange' fontSize='small' />} onClick={handleClickApply}>
                                                    <Span text={t('preliminaryCalculation')} />
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <TabPanel value='1' style={{ height: '100%', padding: 0 }}>
                                        <Engine type={EEngineType.TransversalSectionPrimaryDrainageTrench} onlySlopeAndLoad />
                                    </TabPanel>
                                    {hasSecondaryDrainageTrench && (
                                        <TabPanel value='2' style={{ height: '100%', padding: 0 }}>
                                            <Engine type={EEngineType.TransversalSectionSecondaryDrainageTrench} onlySlopeAndLoad />
                                        </TabPanel>
                                    )}
                                    <TabPanel value='3' style={{ height: '100%', padding: 0 }}>
                                        <Engine type={EEngineType.LongitudinalSection} onlySlopeAndLoad />
                                    </TabPanel>
                                </TabContext>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Stack>
        </DesignContainer>
    );
};

export default isFulfilledProject(Geometry);
